import Enum from '../utils/Enum';
import { WebSocketStatus } from '../utils/enumerations';

import appID from './appID';
import * as actions from '../actions';
import store from '../store';
import * as Sentry from '@sentry/react';


const
    RECONNECT_TIMEOUT = 10 * 1000,
    MAX_ERROR_COUNT = 10,
    CLOSE_PROTOCOL_ERROR = 1002,
    CLOSE_POLICY_VIOLATION = 1008,

    protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:',
    { hostname } = window.location,
    publicLinkIdentifier = (window.location.pathname.match(/^\/public\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/) || [])[1] || '',

    pathname = '/entitychange/listen',

    ErrorCode = Enum('UNINITIALIZED', 'UNAUTHORIZED', 'UNREACHABLE');

class WebSocketConnection {
    constructor(options) {
        this.onOpen = options.onOpen;
        this.onMessage = options.onMessage;
        this.onError = options.onError;

        this._reconnectTimeout = null;

        this._socket = null;

        this.start();
    }

    _onOpen = (event) => {
        this.onOpen(event);
    };

    _onMessage = (event) => {
        this.onMessage(event.data);
    };

    _onError = (_event) => {
        if (this._socket) {
            this._socket = null;

            this.onError(ErrorCode.UNREACHABLE, '');
        }
    };

    _onClose = (event) => {
        if (this._socket) {
            this._socket = null;

            if (event.code === CLOSE_PROTOCOL_ERROR) {
                this.onError(ErrorCode.UNINITIALIZED, event.reason || '');
            }
            else if (event.code === CLOSE_POLICY_VIOLATION) {
                this.onError(ErrorCode.UNAUTHORIZED, event.reason || '');
            }
            else {
                this.onError(ErrorCode.UNREACHABLE, event.reason || '');
            }
        }
    };

    get isRunning() {
        return !!this._socket;
    }

    _connect(url) {
        this._socket = new WebSocket(url);

        this._socket.onopen = this._onOpen;
        this._socket.onmessage = this._onMessage;
        this._socket.onclose = this._onClose;
        this._socket.onerror = this._onError;
    }

    start() {
        this._connect(`${protocol}//${hostname}${pathname}?4S-API-App-ID=${appID}&register=true${publicLinkIdentifier ? `&publicIdentifier=${publicLinkIdentifier}` : ''}`);
    }

    continue() {
        this._connect(`${protocol}//${hostname}${pathname}?4S-API-App-ID=${appID}${publicLinkIdentifier ? `&publicIdentifier=${publicLinkIdentifier}` : ''}`);
    }

    stop() {
        if (this.isRunning) {
            // eslint-disable-next-line func-names, @typescript-eslint/no-empty-function
            this._socket.onclose = function() { };
            this._socket.close();
        }

        // eslint-disable-next-line no-new
        new WebSocket(`${protocol}//${hostname}${pathname}?4S-API-App-ID=${appID}&unregister=true${publicLinkIdentifier ? `&publicIdentifier=${publicLinkIdentifier}` : ''}`);

        if (this._reconnectTimeout) {
            clearTimeout(this._reconnectTimeout);
        }
    }
}

let started = false;
try {
    const ws = new WebSocket(`${protocol}//${hostname}${pathname}?test=true`);
    ws.onclose = () => {
        if (started) { return; }

        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        start(WebSocketConnection);
        started = true;
    };
    ws.onerror = (event) => {
        if (started) { return; }
        if (!DEBUG) {
            Sentry.captureException(new Error(`EntityChange web socket connection encountered an unexpected error when testing the connection :: "${event?.message || ''}"`));
        }

        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        start(WebSocketConnection);
        started = true;
    };
}
catch (error) {
    if (!DEBUG) { Sentry.captureException(new Error(`EntityChange web socket connection failed :: ${error.message}`)); }

    if (!started) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        start(WebSocketConnection);
    }

    started = true;
}

function start(ConnectionConstructor) {
    let errorCount = 0,
        reconnectTimeoutID = null,
        connection = new ConnectionConstructor({
            onOpen() {
                store.dispatch(actions.setWebSocketStatus(WebSocketStatus.CONNECTED));
            },
            onMessage(data) {
                if (data !== 'null') {
                    store.dispatch(actions.addEntityChange(JSON.parse(data)));
                }
                errorCount = 0;
            },
            onError(code, reason) {
                store.dispatch(actions.setWebSocketStatus(WebSocketStatus.DISCONNECTED));

                switch (code) {
                    case ErrorCode.UNINITIALIZED:
                    // TODO notify user
                    // this happens when for some reason server stops storing changes for current client
                    // currently it happens on each server restars since we store changes in it's memory
                    // and it's often annoying so we suppress errors and do not show notifications
                    // but when we have persistent storage this error will appear much more rarely and will mean actual error
                    // showing that local copy of stuff might be out of sync with server and further editing is dangerous
                    // so we should raise it to user and to us somehow

                        // currently just re-register appID on server

                        if (DEBUG) {
                            // eslint-disable-next-line no-console
                            console.warn('EntityChange framework:: Server has restarted, changes might be lost.');
                        }

                        // We receive uninitialized error, when server instance is reloaded
                        // The purpose of this particular session load is to make sure ServiceVersion is up to date
                        // todo: this must be changed when storing entity change queue will be moved to a permanent storage and the queue will stop being dependant on server instance reload
                        store.dispatch(actions.loadSession());

                        connection.start();
                        break;

                    case ErrorCode.UNAUTHORIZED:
                        store.dispatch(actions.voidSession());

                        // eslint-disable-next-line @typescript-eslint/no-use-before-define
                        stop();
                        break;

                    case ErrorCode.UNREACHABLE:
                    // TODO notify user about temporary network problem or full disconnect
                    // either way his attempt to edit stuff will most likely fail

                        if (DEBUG) {
                            // eslint-disable-next-line no-console
                            console.warn('EntityChange framework:: Network error.');
                        }

                        if (errorCount < MAX_ERROR_COUNT) {
                            if (DEBUG) {
                                // eslint-disable-next-line no-console
                                console.warn(`EntityChange framework:: Trying to reconnect in ${RECONNECT_TIMEOUT / 1000} seconds.`);
                            }

                            errorCount++;
                            reconnectTimeoutID = setTimeout(() => connection?.continue(), RECONNECT_TIMEOUT);
                        }

                        break;

                    default:
                        if (!DEBUG) { Sentry.captureException(new Error(`EntityChange web socket has closed. Error code ${code} :: ${reason || 'Unknown reason'}`)); }
                        break;
                }
            }
        });

    function stop() {
        if (reconnectTimeoutID) {
            clearTimeout(reconnectTimeoutID);
            reconnectTimeoutID = null;
        }
        if (connection) {
            connection.stop();
            connection = null;
        }
    }

    window.onbeforeunload = stop;
}
